/**
 * Per-brand sub-region inspect view per scope §6.1.3 (post-scope-doc-
 * clarification commit 2868958). Atlas Day 2.5 P4.
 *
 * Cross-table substrate note: per-brand inspect surfaces brand-level
 * fields only (enforces_map + auto_seeded + is_active + gating_status +
 * return_policy + restocking_fee_pct + marketplace_permissions per
 * FIELD_CLASSIFICATIONS.brands 7 fields). MAP enforcement cross-table
 * substrate references (brands.enforces_map + products.map_price) live
 * at MAP enforcement rule region inspect (Atlas Day 3 §6.6.1); per-brand
 * L11 positional contribution points toward that rule region.
 */

import type { JSX } from 'react';
import { FIELD_CLASSIFICATIONS } from '../../../lib/atlas/classificationMaps';
import { PER_BRAND_PROPAGATION } from '../../../lib/atlas/propagationMaps';
import InspectShell from './InspectShell';
import LayerSection from './LayerSection';
import ComposabilityReservedLayer from './ComposabilityReservedLayer';

export default function PerBrandInspect(): JSX.Element {
  const brandFields = FIELD_CLASSIFICATIONS.brands;
  const fieldEntries = Object.entries(brandFields);

  const strategicFields = fieldEntries.filter(([, c]) => c.layer5Nature === 'strategic');
  const mechanicalFields = fieldEntries.filter(([, c]) => c.layer5Nature === 'mechanical');
  const ownershipFields = fieldEntries.filter(([, c]) => c.layer6Authority === 'ownership');
  const operationalFields = fieldEntries.filter(([, c]) => c.layer6Authority === 'operational');
  const naLayer6Fields = fieldEntries.filter(([, c]) => c.layer6Authority === undefined);

  return (
    <InspectShell
      clusterName="Per-entity configuration"
      subRegionName="Per-brand"
      surfaceLine="ISG carries N brands; K active for buying. MAP enforcement configured per brand → MAP rule above."
    >
      <LayerSection layer={4} title="Math vs judgment">
        <p>
          Per scope §6.1.3: <code>enforces_map</code> = <strong>judgment</strong> (ISG&apos;s
          per-brand MAP-enforcement stance); <code>auto_seeded</code> ={' '}
          <strong>mechanical</strong> (Algopix-discovery flag); <code>is_active</code> ={' '}
          <strong>judgment</strong>; <code>gating_status</code> = <strong>judgment</strong>{' '}
          (currently vestigial per D2 lock — all 911 brands uniform &quot;open&quot;).
        </p>
        <p>
          MAP price comparison (cross-table arithmetic: sale_price vs products.map_price) is
          math-pattern; lives at MAP enforcement rule region (Atlas Day 3 §6.6.1) not per-brand.
        </p>
      </LayerSection>

      <LayerSection layer={5} title="Mechanical vs strategic">
        <div>
          <h3 className="text-xs font-semibold text-ink-bright mb-1">
            Strategic section ({strategicFields.length} fields)
          </h3>
          <ul className="list-disc pl-5 text-xs space-y-0.5">
            {strategicFields.map(([field, c]) => (
              <li key={field}>
                <code>{field}</code>
                {c.rationale ? ` — ${c.rationale.split(';')[0]}` : ''}
              </li>
            ))}
          </ul>
        </div>
        <div className="mt-3">
          <h3 className="text-xs font-semibold text-ink-bright mb-1">
            Mechanical section ({mechanicalFields.length} fields)
          </h3>
          <ul className="list-disc pl-5 text-xs space-y-0.5">
            {mechanicalFields.map(([field, c]) => (
              <li key={field}>
                <code>{field}</code>
                {c.rationale ? ` — ${c.rationale.split(';')[0]}` : ''}
              </li>
            ))}
          </ul>
        </div>
      </LayerSection>

      <LayerSection layer={6} title="Operational vs ownership">
        <div>
          <h3 className="text-xs font-semibold text-ink-bright mb-1">
            Ownership-altitude ({ownershipFields.length} fields) — define per-brand stance
          </h3>
          <ul className="list-disc pl-5 text-xs space-y-0.5">
            {ownershipFields.map(([field]) => (
              <li key={field}>
                <code>{field}</code>
              </li>
            ))}
          </ul>
        </div>
        <div className="mt-3">
          <h3 className="text-xs font-semibold text-ink-bright mb-1">
            Operational-altitude ({operationalFields.length} fields)
          </h3>
          <ul className="list-disc pl-5 text-xs space-y-0.5">
            {operationalFields.map(([field]) => (
              <li key={field}>
                <code>{field}</code>
              </li>
            ))}
          </ul>
        </div>
        {naLayer6Fields.length > 0 && (
          <div className="mt-3">
            <h3 className="text-xs font-semibold text-ink-bright mb-1">
              Region-altitude ({naLayer6Fields.length} fields, layer 6 N/A per three-state
              applicability)
            </h3>
            <ul className="list-disc pl-5 text-xs space-y-0.5">
              {naLayer6Fields.map(([field, c]) => (
                <li key={field}>
                  <code>{field}</code>
                  {c.rationale ? ` — ${c.rationale.split(';')[0]}` : ''}
                </li>
              ))}
            </ul>
          </div>
        )}
      </LayerSection>

      <LayerSection layer={7} title="Trace">
        <p>
          Per scope §6.1.3 L7 (post-clarification): <code>brands.provenance</code> covers
          brand-level field changes (Day 67 P2.2 substrate + Day 82 P6.1 triggers cover
          enforces_map). <code>products.map_price</code> trace lives at{' '}
          <code>products.provenance</code> (Algopix-sourced via useAlgopixImport with
          provenance write) — surfaces at MAP enforcement rule region inspect, not here.
        </p>
      </LayerSection>

      <LayerSection layer={8} title="Propagation">
        <p>Curated propagation map (verified as of 2026-05-27 per scope §8 staleness marker):</p>
        <ul className="list-disc pl-5 text-xs space-y-2">
          {PER_BRAND_PROPAGATION.map((entry) => (
            <li key={entry.field}>
              <strong>
                <code>{entry.field}</code>
              </strong>{' '}
              edits affect:
              <ul className="list-circle pl-5 mt-1 space-y-0.5">
                {entry.affects.map((eff) => (
                  <li key={eff}>{eff}</li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </LayerSection>

      <LayerSection layer={9} title="Reversibility">
        <p>
          Per scope §6.1.3 L9: reversible-with-residue for ownership-altitude fields
          (enforces_map edits propagate to future calculate_profitability evaluations; past
          below_map flags persist on products from prior enforcement window). Per-field:
        </p>
        <ul className="list-disc pl-5 text-xs space-y-0.5">
          {fieldEntries.map(([field, c]) => (
            <li key={field}>
              <code>{field}</code> — <span className="font-mono">{c.layer9Reversibility}</span>
            </li>
          ))}
        </ul>
      </LayerSection>

      <ComposabilityReservedLayer />

      <LayerSection layer={11} title="Positional contribution">
        <p>
          Substantive-contribution pattern per scope §6.1.3 L11. Per-brand{' '}
          <code>enforces_map</code> contributes to ISG&apos;s position by encoding
          MAP-violation flagging stance per manufacturer relationship; surfaces in title block
          sentence 2 (Atlas Day 1 substrate-driven render: &quot;has not yet configured MAP
          enforcement&quot; at current substrate state per D5 conditional). Points toward MAP
          enforcement rule region (Atlas Day 3 §6.6.1) for cross-table substrate detail.
        </p>
      </LayerSection>
    </InspectShell>
  );
}
